import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageBody, PageHeader } from '../ui';
import AccountInstructors from './AccountInstructors';
import Cancellations from './Cancellations';
import Exceptions from './Exceptions';
import InstructorLoad from './InstructorLoad';
import InvoiceQueue from './InvoiceQueue';

type Tab = 'exceptions' | 'load' | 'accounts' | 'cancellations' | 'invoices';

const TABS: { key: Tab; label: string; hint: string }[] = [
  {
    key: 'exceptions',
    label: 'Exceptions',
    hint: 'Six lists, done when empty',
  },
  {
    key: 'load',
    label: 'Instructor load',
    hint: 'Classes, registrations, hours',
  },
  {
    key: 'accounts',
    label: 'Account instructors',
    hint: 'Who has taught whom',
  },
  {
    key: 'cancellations',
    label: 'Cancellations',
    hint: 'Cancelled public registrations by account; escalate',
  },
  {
    key: 'invoices',
    label: 'Invoice queue',
    hint: 'What finance still has to act on',
  },
];

/**
 * Reports — the counts Eddie pulls by hand and the monitors that used to
 * be Salesforce list views, plus the cancellation review and the invoice
 * queue. Five tabs, kept on the URL so a link lands on the right one.
 */
const OpsReports: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab =
    raw === 'load' ||
    raw === 'accounts' ||
    raw === 'cancellations' ||
    raw === 'invoices'
      ? raw
      : 'exceptions';

  return (
    <>
      <PageHeader
        title="Reports"
        meta="Counts and queues. Every seat figure is a registration; nothing here scores an instructor."
      />
      <div className="px-8 pb-4">
        <div className="inline-flex rounded-lg border border-white-regular bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setParams({ tab: t.key })}
              className={`rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                tab === t.key
                  ? 'bg-blue-100 font-semibold text-blue-primary'
                  : 'text-gray-three hover:text-gray-four'
              }`}
              title={t.hint}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <PageBody wide>
        {tab === 'exceptions' && <Exceptions />}
        {tab === 'load' && <InstructorLoad />}
        {tab === 'accounts' && <AccountInstructors />}
        {tab === 'cancellations' && <Cancellations />}
        {tab === 'invoices' && <InvoiceQueue />}
      </PageBody>
    </>
  );
};

export default OpsReports;
